// Fase A · Exámenes DGT · diálogo de registro de resultado.
// -----------------------------------------------------------------------------
// Se abre al revisar una cita de examen atendida. El admin marca:
//   · APTO
//   · NO APTO
//   · NO PRESENTADO (con checkbox "justificado")
//   · CONVOCATORIA ANULADA POR DGT
//
// El backend hace las consecuencias: penalización, cambio a APTO,
// cierre de expediente y auditoría mínima.
// -----------------------------------------------------------------------------
import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Trophy,
  CheckCircle2,
  XCircle,
  UserX,
  Ban,
  Loader2,
} from "lucide-react";

const OPTIONS = [
  { key: "apto",         label: "APTO",                   Icon: CheckCircle2, cls: "border-emerald-300 hover:bg-emerald-50 text-emerald-800" },
  { key: "no_apto",      label: "NO APTO",                Icon: XCircle,      cls: "border-rose-300 hover:bg-rose-50 text-rose-800" },
  { key: "no_presentado",label: "No presentado",          Icon: UserX,        cls: "border-amber-300 hover:bg-amber-50 text-amber-800" },
  { key: "anulado_dgt",  label: "Convocatoria anulada",   Icon: Ban,          cls: "border-neutral-300 hover:bg-neutral-50 text-neutral-800" },
];

export default function ExamResultDialog({ open, appointment, onOpenChange, onSaved }) {
  const [choice, setChoice] = useState("");
  const [justified, setJustified] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setChoice(""); setJustified(false); };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSubmit = async () => {
    if (!choice || !appointment?.id) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/appointments/${appointment.id}/result`, {
        result: choice,
        justified: choice === "no_presentado" ? justified : false,
      });
      if (data?.aprobado) {
        toast.success("¡APTO! Expediente cerrado en verde.");
      } else if (choice === "no_apto" && data?.student_updates?.next_exam_allowed_from) {
        toast.info(`Suspenso registrado. Próximo examen: ${data.student_updates.next_exam_allowed_from}`);
      } else {
        toast.success("Resultado registrado");
      }
      onSaved?.();
      handleClose(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo registrar el resultado");
    } finally {
      setSubmitting(false);
    }
  };

  const kindLabel = appointment?.appointment_type === "examen_practico" ? "PRÁCTICO" : "TEÓRICO";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="exam-result-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-orange-600" />
            Resultado del examen {kindLabel}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {appointment?.student?.name || appointment?.student_id} · {appointment?.date} · {appointment?.time}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-2">
            {OPTIONS.map(({ key, label, Icon, cls }) => (
              <button
                key={key}
                type="button"
                onClick={() => setChoice(key)}
                data-testid={`exam-result-${key}`}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-3 text-sm font-semibold transition ${cls} ${
                  choice === key ? "ring-2 ring-orange-500 bg-orange-50" : ""
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {choice === "no_presentado" && (
            <label className="flex items-start gap-2 text-sm p-3 rounded-lg bg-amber-50 border border-amber-200">
              <input
                type="checkbox"
                checked={justified}
                onChange={(e) => setJustified(e.target.checked)}
                data-testid="exam-result-justified"
                className="mt-0.5"
              />
              <span>
                <span className="font-semibold text-amber-900">Justificado</span>{" "}
                <span className="text-amber-800">
                  · No consumirá convocatoria (ej. baja médica documentada).
                </span>
              </span>
            </label>
          )}

          {choice === "no_apto" && (
            <div className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-lg p-2">
              El sistema calculará automáticamente la fecha mínima para el siguiente examen según el número de suspensos del expediente.
            </div>
          )}

          {choice === "apto" && appointment?.appointment_type === "examen_practico" && (
            <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2 font-semibold">
              Al confirmar, el alumno pasará a estado APTO y su expediente quedará cerrado en verde.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => handleClose(false)}
              data-testid="exam-result-cancel"
              className="px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!choice || submitting}
              data-testid="exam-result-confirm"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Guardando…</> : "Registrar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
